import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Button, Card, EmptyState, Input, NoticeBox, Pagination, Select, Tags, Trash2 } from "../../ui";
import { DeleteModal } from "../../modals/Delete";
import DashboardModal from "../../modals/DashboardModal";
import IssueInvoiceModal from "../admin/modals/IssueInvoiceModal";
import ChangePlanModal from "../admin/modals/ChangePlanModal";
import AddBranchModal from "../admin/modals/AddBranchModal";
import ExtendCoverageModal from "../admin/modals/ExtendCoverageModal";
import "../admin/BillingAdmin.css";
import "./AccountantView.css";
import { exportDashboardRowsToExcel } from "../../../utils/ludDashboardExcel";
import { formatSarLatin } from "../../../utils/billingMoney";
import { addSubscriptionBranchAPI, extendSubscriptionBranchAPI } from "../../../utils/api/billing";
import useAccountantBilling from "./useAccountantBilling";
import useAccountantDirectory from "./useAccountantDirectory";
import { AccountantBarChart, AccountantDonutChart } from "./AccountantCharts";
import { accountantText } from "./accountantText";

const CURRENT_YEAR = String(new Date().getFullYear());
const PAGE_SIZE = 20;
const INVOICE_STATUSES = ["unpaid", "partial", "review", "paid", "cancelled"];
const STATUS_VARIANT = { unpaid: "warning", partial: "warning", review: "info", paid: "success", cancelled: "danger" };
const SUBSCRIPTION_CATEGORIES = ["expired", "nearExpiry", "valid", "pending"];
const CATEGORY_VARIANT = { expired: "danger", nearExpiry: "warning", valid: "success", pending: "warning" };
const SORT_COLUMNS = ["date", "dueDate", "client", "number", "amount", "paid", "remaining", "status"];

// Billing amounts are in minor units (halalas); charts and exports use SAR.
const toSar = (minor) => Number(minor || 0) / 100;

// Summary value for a billing resource: "…" while loading, "—" on failure, otherwise the server figure (0 is a real value).
const resourceValue = (resource, pick) =>
  resource.status === "ready" && resource.data ? pick(resource.data) : resource.status === "error" ? "—" : "…";

const downloadExcelFile = (filename, rows) => {
  exportDashboardRowsToExcel({ filename, sheetName: "Invoices", rows });
};

function Kpi({ label, value, tone = "primary", primary = false }) {
  return (
    <div className={`accountant-kpi is-${tone}${primary ? " is-primary" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function FilterChips({ label, options, value, onChange }) {
  return (
    <div className="accountant-chips" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          type="button"
          key={option.key}
          className={value === option.key ? "is-active" : ""}
          aria-pressed={value === option.key}
          onClick={() => onChange(option.key)}
        >
          {option.label}
          <b>{option.count}</b>
        </button>
      ))}
    </div>
  );
}

function SortHeader({ column, label, sort, onSort, numeric = false }) {
  const active = sort.column === column;
  const Icon = !active ? ChevronsUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={numeric ? "is-numeric" : ""} aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(column)}>
        {label}
        <Icon size={13} aria-hidden="true" />
      </button>
    </th>
  );
}

export default function AccountantView({ lang: langProp, embedded = false }) {
  const lang = langProp === "en" ? "en" : "ar";
  const t = accountantText(lang);
  const sar = (minor) => formatSarLatin(minor, lang);

  // "" = every restaurant the user may access (the server applies the scope).
  const [restaurant, setRestaurant] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [analyticsYear, setAnalyticsYear] = useState(CURRENT_YEAR);
  const [analyticsMonth, setAnalyticsMonth] = useState("");
  const [paymentPackage, setPaymentPackage] = useState("");
  const [paymentFrom, setPaymentFrom] = useState("");
  const [paymentTo, setPaymentTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ column: "date", direction: "desc" });
  const [invoicePage, setInvoicePage] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [subscriptionPage, setSubscriptionPage] = useState(1);
  const [issueInvoiceOpen, setIssueInvoiceOpen] = useState(false);
  // Official billing workflow currently open: { type, subscription, branch }.
  const [subscriptionModal, setSubscriptionModal] = useState(null);
  const [addBranchChoice, setAddBranchChoice] = useState({});
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteError, setNoteError] = useState(false);
  const [noteDeleteId, setNoteDeleteId] = useState(null);

  const directory = useAccountantDirectory(restaurant);
  const RESTAURANTS = directory.stores;

  // Subscription, invoice and analytics data all come from the billing models via the accountant endpoints.
  const billing = useAccountantBilling({
    storeId: restaurant,
    invoiceFilters: { package: paymentPackage, from: paymentFrom, to: paymentTo, search: debouncedSearch },
    analyticsPeriod: { year: analyticsYear, month: analyticsMonth },
  });
  const subscriptionsData = billing.subscriptions.data;
  const invoicesData = billing.invoices.data;
  const analyticsData = billing.analytics.data;
  const capabilities = subscriptionsData?.capabilities || invoicesData?.capabilities || {};
  const nearExpiryDays = subscriptionsData?.nearExpiryDays;
  const financialSummary = invoicesData?.summary;
  const total = financialSummary?.totalMinor || 0;
  const collected = financialSummary?.collectedMinor || 0;

  const storeName = (store) => (lang === "ar" ? store.nameAr : store.nameEn);
  const restaurantOf = (id, fallbackName) =>
    RESTAURANTS.find((item) => item.id === id) || { id, nameAr: fallbackName || id, nameEn: fallbackName || id };
  const selectedRestaurant = RESTAURANTS.find((item) => item.id === restaurant) || restaurantOf(restaurant);

  useEffect(() => {
    setInvoicePage(1);
  }, [restaurant, paymentPackage, paymentFrom, paymentTo, debouncedSearch, statusFilter, sort]);
  useEffect(() => {
    setSubscriptionPage(1);
  }, [restaurant, categoryFilter]);

  const resetPaymentFilters = () => {
    setPaymentPackage("");
    setPaymentFrom("");
    setPaymentTo("");
    setSearch("");
    setStatusFilter("all");
  };

  // ---- invoices ---------------------------------------------------------

  const invoiceRows = useMemo(() => invoicesData?.rows || [], [invoicesData]);

  const sortedInvoices = useMemo(() => {
    const valueOf = (row) => {
      if (sort.column === "client") {
        const store = RESTAURANTS.find((item) => item.id === row.restaurantId);
        return store ? (lang === "ar" ? store.nameAr : store.nameEn) : row.restaurantId;
      }
      if (sort.column === "amount") return row.amountMinor;
      if (sort.column === "paid") return row.paidMinor;
      if (sort.column === "remaining") return row.remainingMinor;
      if (sort.column === "status") return INVOICE_STATUSES.indexOf(row.status);
      return row[sort.column] || "";
    };
    const factor = sort.direction === "asc" ? 1 : -1;
    return invoiceRows
      .filter((row) => statusFilter === "all" || row.status === statusFilter)
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const left = valueOf(a.row);
        const right = valueOf(b.row);
        const order = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right), lang);
        return order * factor || a.index - b.index;
      })
      .map(({ row }) => row);
  }, [invoiceRows, statusFilter, sort, RESTAURANTS, lang]);

  const invoicePages = Math.max(1, Math.ceil(sortedInvoices.length / PAGE_SIZE));
  const shownInvoices = sortedInvoices.slice((invoicePage - 1) * PAGE_SIZE, invoicePage * PAGE_SIZE);

  const onSort = (column) =>
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: ["date", "dueDate", "amount", "paid", "remaining"].includes(column) ? "desc" : "asc" },
    );

  const statusCounts = invoicesData?.summary?.byStatus || {};
  const statusOptions = [
    { key: "all", label: t.invoices.statusAll, count: financialSummary?.invoiceCount ?? 0 },
    ...INVOICE_STATUSES.map((status) => ({ key: status, label: t.status[status], count: statusCounts[status] || 0 })),
  ];

  const exportPayments = () => {
    const statusOrder = { unpaid: 1, partial: 2, review: 3, paid: 4, cancelled: 5 };
    const rows = [...invoiceRows]
      .sort(
        (first, second) =>
          statusOrder[first.status] - statusOrder[second.status] || String(second.date || "").localeCompare(String(first.date || "")),
      )
      .map((invoice) => {
        const store = restaurantOf(invoice.restaurantId);
        return [
          storeName(store),
          store.id,
          invoice.number,
          invoice.package || "",
          invoice.date || "",
          toSar(invoice.amountMinor),
          toSar(invoice.paidMinor),
          toSar(invoice.remainingMinor),
          t.status[invoice.status] || invoice.status,
          invoice.paymentDate || "",
          invoice.dueDate || "",
        ];
      });
    const c = t.invoices.columns;
    downloadExcelFile("lud-invoices-full.xlsx", [
      [c.client, t.followUp.clientId, c.number, c.package, c.date, c.amount, c.paid, c.remaining, c.status, c.paymentDate, c.dueDate],
      ...rows,
    ]);
  };

  // ---- subscriptions ----------------------------------------------------

  const allSubscriptions = subscriptionsData?.subscriptions || [];
  const expirySummary = subscriptionsData?.summary || { expired: 0, nearExpiry: 0, valid: 0, pending: 0, active: 0 };
  const filteredSubscriptions = allSubscriptions.filter((item) => categoryFilter === "all" || item.category === categoryFilter);
  const subscriptionPages = Math.max(1, Math.ceil(filteredSubscriptions.length / PAGE_SIZE));
  const shownSubscriptions = filteredSubscriptions.slice((subscriptionPage - 1) * PAGE_SIZE, subscriptionPage * PAGE_SIZE);
  const categoryOptions = [
    { key: "all", label: t.subscriptions.all, count: allSubscriptions.length },
    ...SUBSCRIPTION_CATEGORIES.map((category) => ({
      key: category,
      label: t.subscriptions.category[category],
      count: expirySummary[category] || 0,
    })),
  ];

  // remainingDays is computed by the backend from the authoritative end date and is never negative.
  const expiryDaysText = (item) => {
    if (item.category === "pending") return t.subscriptions.category.pending;
    if (item.remainingDays === null || item.remainingDays === undefined) return "—";
    if (item.category === "expired") return t.subscriptions.expiredOn(item.endDate);
    return t.subscriptions.remaining(item.remainingDays);
  };

  const toBillingSubscription = (subscription) => ({
    _id: subscription.id,
    type: subscription.type,
    status: subscription.status,
    planSnapshot: {
      code: subscription.plan?.code,
      version: subscription.plan?.version,
      billingCycle: subscription.plan?.billingCycle,
    },
    branchCoverage: subscription.branches,
  });

  const closeSubscriptionModal = () => setSubscriptionModal(null);

  const confirmAddBranch = async ({ startDate, endDate }) => {
    const { subscription, branch } = subscriptionModal;
    await addSubscriptionBranchAPI(billing.token, subscription.id, branch.branchId, { startDate, endDate });
    closeSubscriptionModal();
    billing.reload();
  };

  const confirmExtendBranch = async (endDate) => {
    const { subscription, branch } = subscriptionModal;
    await extendSubscriptionBranchAPI(billing.token, subscription.id, branch.branchId, { endDate });
    closeSubscriptionModal();
    billing.reload();
  };

  // ---- analytics --------------------------------------------------------

  const statusItems = useMemo(() => {
    const counts = analyticsData?.statusDistribution || {};
    return ["paid", "unpaid", "partial", "review"].map((key) => ({ key, label: t.status[key], value: counts[key] || 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsData, lang]);

  const monthlyRevenue = useMemo(() => {
    const totals = (analyticsData?.monthlyCollectedMinor || Array(12).fill(0)).map(toSar);
    if (analyticsMonth) {
      const monthIndex = Number(analyticsMonth) - 1;
      return { labels: [t.analytics.months[monthIndex]], values: [totals[monthIndex]] };
    }
    return { labels: t.analytics.months, values: totals };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsData, analyticsMonth, lang]);

  const packageRevenue = useMemo(() => {
    const packages = analyticsData?.revenueByPackageMinor || {};
    const labels = ["L", "U", "D", t.analytics.integration];
    const values = [packages.L, packages.U, packages.D, packages.INTG].map(toSar);
    // Invoices without a plan (e.g. services) are shown separately instead of being attributed to a package they do not belong to.
    if (packages.other > 0) {
      labels.push(t.analytics.other);
      values.push(toSar(packages.other));
    }
    return { labels, values };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsData, lang]);

  const agingValues = useMemo(() => (analyticsData?.agingMinor || [0, 0, 0, 0]).map(toSar), [analyticsData]);

  const analyticsYears = useMemo(
    () => [...new Set([...(analyticsData?.years || []).map(String), analyticsYear])].sort(),
    [analyticsData, analyticsYear],
  );

  // ---- client follow-up and notes ---------------------------------------

  const followUp = invoicesData?.clientFollowUp;
  const pendingCount = followUp ? followUp.pendingCount : billing.invoices.status === "error" ? "—" : "…";
  const currentPackage = subscriptionsData?.currentPackages?.[restaurant] || "—";
  const restaurantsInView = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return RESTAURANTS.length;
    return RESTAURANTS.filter((item) => `${item.id} ${item.nameAr} ${item.nameEn}`.toLowerCase().includes(query)).length;
  }, [RESTAURANTS, search]);

  const exportStoreData = () => {
    const f = t.followUp;
    const rows = restaurant
      ? [
          [f.client, storeName(selectedRestaurant)],
          [f.clientId, selectedRestaurant.id],
          [f.city, selectedRestaurant.city || ""],
          [f.package, currentPackage],
          [f.pending, pendingCount],
          [f.lastPayment, followUp?.lastPaymentDate || "—"],
        ]
      : [
          [f.restaurants, restaurantsInView],
          [f.pending, pendingCount],
          [f.package, "—"],
        ];
    downloadExcelFile(`lud-store-data-${restaurant ? selectedRestaurant.id : "all"}.xlsx`, [[f.field, f.value], ...rows]);
  };

  const notes = directory.notes.rows.map((note) => ({
    ...note,
    id: String(note._id || note.id),
    text: note.note || note.text || "",
    date: String(note.createdAt || note.date || "").slice(0, 10),
  }));

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    try {
      await directory.createNote(text);
      setNoteText("");
      setNoteError(false);
      setNoteModalOpen(false);
    } catch {
      setNoteError(true);
    }
  };

  const deleteNote = async () => {
    if (!noteDeleteId) return;
    try {
      await directory.deleteNote(noteDeleteId);
      setNoteError(false);
    } catch {
      setNoteError(true);
    }
    setNoteDeleteId(null);
  };

  const paginationLabels = { ...t.pagination, limit: "" };
  const retry = (
    <Button size="sm" appearance="outline" onClick={billing.reload}>
      {t.retry}
    </Button>
  );
  const c = t.invoices.columns;
  const invoicesLoading = billing.invoices.status === "loading" && !invoicesData;

  return (
    <div className="accountant-view" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="accountant-toolbar">
        <div className="accountant-toolbar-restaurant">
          <Select
            label={t.restaurant}
            value={restaurant}
            onChange={(event) => setRestaurant(event.target.value)}
            options={[
              { value: "", label: t.allRestaurants },
              ...RESTAURANTS.map((item) => ({ value: item.id, label: storeName(item) })),
            ]}
          />
        </div>
        <div className="accountant-toolbar-actions accountant-no-print">
          <Button appearance="outline" onClick={() => window.print()}>
            {t.print}
          </Button>
          {!embedded && (
            <Button appearance="outline" onClick={billing.reload}>
              {t.refresh}
            </Button>
          )}
          {/* Inside Billing administration the page header already holds this action. */}
          {!embedded && capabilities.canIssueInvoice && (
            <Button appearance="filled" onClick={() => setIssueInvoiceOpen(true)}>
              {t.invoices.issue}
            </Button>
          )}
        </div>
      </div>

      {directory.storesError && (
        <NoticeBox variant="warning" size="sm">
          {t.restaurantsError}
        </NoticeBox>
      )}

      <section className="accountant-section" aria-labelledby="accountant-overview">
        <div className="accountant-section-head">
          <div>
            <h2 id="accountant-overview">{t.overview.title}</h2>
            <p>{t.overview.filtered}</p>
          </div>
        </div>
        <div className="accountant-kpis">
          <Kpi primary label={t.overview.total} value={resourceValue(billing.invoices, () => sar(total))} />
          <Kpi primary tone="success" label={t.overview.collected} value={resourceValue(billing.invoices, () => sar(collected))} />
          <Kpi
            primary
            tone="danger"
            label={t.overview.outstanding}
            value={resourceValue(billing.invoices, (data) => sar(data.summary.outstandingMinor))}
          />
          <Kpi primary label={t.overview.invoices} value={resourceValue(billing.invoices, (data) => data.summary.invoiceCount)} />
        </div>
        <dl className="accountant-kpi-details">
          <div>
            <dt>{t.overview.rate}</dt>
            <dd>{resourceValue(billing.invoices, () => `${total ? Math.round((collected / total) * 100) : 0}%`)}</dd>
          </div>
          <div>
            <dt>{t.overview.partial}</dt>
            <dd>{resourceValue(billing.invoices, (data) => sar(data.summary.partialPaidMinor))}</dd>
          </div>
          <div>
            <dt>{t.overview.review}</dt>
            <dd>{resourceValue(billing.invoices, (data) => sar(data.summary.pendingReviewMinor))}</dd>
          </div>
        </dl>
      </section>

      <section className="accountant-section" aria-labelledby="accountant-invoices">
        <div className="accountant-section-head">
          <div>
            <h2 id="accountant-invoices">{t.invoices.title}</h2>
            <p>{t.invoices.description}</p>
          </div>
          <Button appearance="outline" className="accountant-no-print" disabled={!invoiceRows.length} onClick={exportPayments}>
            {t.invoices.export}
          </Button>
        </div>

        <div className="accountant-filters accountant-no-print">
          <Input value={search} label={t.invoices.search} onChange={(event) => setSearch(event.target.value)} />
          <Select
            label={t.invoices.package}
            value={paymentPackage}
            onChange={(event) => setPaymentPackage(event.target.value)}
            options={[
              { value: "", label: t.invoices.packageAll },
              { value: "L", label: "L" },
              { value: "U", label: "U" },
              { value: "D", label: "D" },
              { value: "INTG", label: t.analytics.integration },
            ]}
          />
          <Input type="date" label={t.invoices.from} value={paymentFrom} onChange={(event) => setPaymentFrom(event.target.value)} />
          <Input type="date" label={t.invoices.to} value={paymentTo} onChange={(event) => setPaymentTo(event.target.value)} />
          <Button appearance="soft" variant="neutral" onClick={resetPaymentFilters}>
            {t.invoices.clear}
          </Button>
        </div>

        <FilterChips label={c.status} options={statusOptions} value={statusFilter} onChange={setStatusFilter} />

        <div className="accountant-mobile-sort accountant-no-print">
          <Select
            label={t.invoices.sortBy}
            value={sort.column}
            onChange={(event) => onSort(event.target.value)}
            options={SORT_COLUMNS.map((column) => ({ value: column, label: c[column] }))}
          />
        </div>

        {invoicesData?.truncated && (
          <NoticeBox variant="info" size="sm">
            {t.invoices.truncated(invoiceRows.length)}
          </NoticeBox>
        )}

        {billing.invoices.status === "error" ? (
          <EmptyState variant="error" compact action={retry}>
            {t.invoices.error}
          </EmptyState>
        ) : invoicesLoading ? (
          <EmptyState variant="loading" compact>
            {t.loading}
          </EmptyState>
        ) : !sortedInvoices.length ? (
          <EmptyState compact>{t.invoices.empty}</EmptyState>
        ) : (
          <>
            <div className="accountant-table-wrap" aria-busy={billing.invoices.status === "loading"}>
              <table className="accountant-table">
                <thead>
                  <tr>
                    <SortHeader column="client" label={c.client} sort={sort} onSort={onSort} />
                    <SortHeader column="number" label={c.number} sort={sort} onSort={onSort} />
                    <th>{c.package}</th>
                    <SortHeader column="date" label={c.date} sort={sort} onSort={onSort} />
                    <SortHeader column="dueDate" label={c.dueDate} sort={sort} onSort={onSort} />
                    <SortHeader column="amount" label={c.amount} sort={sort} onSort={onSort} numeric />
                    <SortHeader column="paid" label={c.paid} sort={sort} onSort={onSort} numeric />
                    <SortHeader column="remaining" label={c.remaining} sort={sort} onSort={onSort} numeric />
                    <SortHeader column="status" label={c.status} sort={sort} onSort={onSort} />
                  </tr>
                </thead>
                <tbody>
                  {shownInvoices.map((invoice) => {
                    const store = restaurantOf(invoice.restaurantId);
                    return (
                      <tr key={invoice.id}>
                        <td data-label={c.client}>
                          <span className="accountant-client">
                            <strong>{storeName(store)}</strong>
                            <small>{store.id}</small>
                          </span>
                        </td>
                        <td data-label={c.number}>{invoice.number || "—"}</td>
                        <td data-label={c.package}>{invoice.package || "—"}</td>
                        <td data-label={c.date}>{invoice.date || "—"}</td>
                        <td data-label={c.dueDate}>{invoice.dueDate || "—"}</td>
                        <td data-label={c.amount} className="is-numeric">{sar(invoice.amountMinor)}</td>
                        <td data-label={c.paid} className="is-numeric">{sar(invoice.paidMinor)}</td>
                        <td data-label={c.remaining} className={`is-numeric${invoice.remainingMinor > 0 ? " is-due" : ""}`}>
                          {sar(invoice.remainingMinor)}
                        </td>
                        <td data-label={c.status}>
                          <Tags variant={STATUS_VARIANT[invoice.status] || "neutral"} appearance="soft" size="sm">
                            {t.status[invoice.status] || invoice.status}
                          </Tags>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {invoicePages > 1 && (
              <Pagination
                className="accountant-no-print"
                page={Math.min(invoicePage, invoicePages)}
                pages={invoicePages}
                total={sortedInvoices.length}
                onPage={setInvoicePage}
                labels={paginationLabels}
              />
            )}
          </>
        )}

        {issueInvoiceOpen && (
          <IssueInvoiceModal
            onCancel={() => setIssueInvoiceOpen(false)}
            onIssued={() => {
              setIssueInvoiceOpen(false);
              billing.reload();
            }}
          />
        )}
      </section>

      <section className="accountant-section" aria-labelledby="accountant-subscriptions">
        <div className="accountant-section-head">
          <div>
            <h2 id="accountant-subscriptions">{t.subscriptions.title}</h2>
            <p>{t.subscriptions.description(nearExpiryDays)}</p>
          </div>
          <span className="accountant-count">
            {t.subscriptions.active}: <b>{resourceValue(billing.subscriptions, (data) => data.summary.active)}</b>
          </span>
        </div>

        <FilterChips label={t.subscriptions.title} options={categoryOptions} value={categoryFilter} onChange={setCategoryFilter} />

        {billing.subscriptions.status === "error" ? (
          <EmptyState variant="error" compact action={retry}>
            {t.subscriptions.error}
          </EmptyState>
        ) : billing.subscriptions.status === "loading" && !subscriptionsData ? (
          <EmptyState variant="loading" compact>
            {t.loading}
          </EmptyState>
        ) : !filteredSubscriptions.length ? (
          <EmptyState compact>{t.subscriptions.empty}</EmptyState>
        ) : (
          <div className="accountant-subscriptions">
            {shownSubscriptions.map((subscription) => {
              const store = restaurantOf(subscription.restaurantId, subscription.restaurantName);
              const isCurrent = !["draft", "canceled"].includes(subscription.status);
              const selectedNewBranch = addBranchChoice[subscription.id] || subscription.uncoveredBranches[0]?.branchId || "";
              const typeLabel = subscription.type === "integration" ? t.subscriptions.integration : t.subscriptions.platform;
              return (
                <details className={`accountant-subscription is-${subscription.category}`} key={subscription.id}>
                  <summary>
                    <span className="accountant-client">
                      <strong>{storeName(store)}</strong>
                      <small>
                        {store.id} · {typeLabel} · {t.subscriptions.package} {subscription.plan?.code || "—"}
                      </small>
                    </span>
                    <span className="accountant-subscription-end">{expiryDaysText(subscription)}</span>
                    <Tags variant={CATEGORY_VARIANT[subscription.category] || "success"} appearance="soft" size="sm">
                      {t.subscriptions.category[subscription.category] || subscription.category}
                    </Tags>
                  </summary>

                  <div className="accountant-subscription-body">
                    <dl className="accountant-facts">
                      <div>
                        <dt>{t.subscriptions.package}</dt>
                        <dd>
                          {subscription.plan
                            ? `${subscription.plan.code} — ${lang === "ar" ? subscription.plan.nameAr : subscription.plan.nameEn}`
                            : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>{t.subscriptions.from}</dt>
                        <dd>{subscription.startDate || "—"}</dd>
                      </div>
                      <div>
                        <dt>{t.subscriptions.to}</dt>
                        <dd>{subscription.endDate || "—"}</dd>
                      </div>
                    </dl>

                    {subscription.pendingPlanChange && (
                      <p className="accountant-muted">
                        {t.subscriptions.scheduledChange(
                          subscription.pendingPlanChange.planCode,
                          subscription.pendingPlanChange.effectiveAt,
                        )}
                      </p>
                    )}

                    {capabilities.canChangeSubscription && subscription.status === "active" && !subscription.pendingPlanChange && (
                      <div>
                        <Button size="sm" appearance="soft" onClick={() => setSubscriptionModal({ type: "changePlan", subscription })}>
                          {t.subscriptions.changePlan}
                        </Button>
                      </div>
                    )}

                    {subscription.branches.length > 0 && (
                      <div className="accountant-branches">
                        <h3>{t.subscriptions.branches}</h3>
                        {subscription.branches.map((branch) => (
                          <div className="accountant-branch" key={branch.branchId}>
                            <strong>{branch.branchName}</strong>
                            {branch.status !== "active" ? (
                              <Tags variant="warning" appearance="soft" size="sm">
                                {t.subscriptions.branchStatus[branch.status] || branch.status}
                              </Tags>
                            ) : (
                              <>
                                <span className="accountant-branch-dates">
                                  {branch.startDate || "—"} – {branch.endDate || subscription.endDate || "—"}
                                </span>
                                <span>{branch.remainingDays === null ? "—" : t.subscriptions.remaining(branch.remainingDays)}</span>
                                {capabilities.canChangeSubscription && isCurrent && (
                                  <Button
                                    size="sm"
                                    appearance="soft"
                                    onClick={() => setSubscriptionModal({ type: "extendBranch", subscription, branch })}
                                  >
                                    {t.subscriptions.extend}
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {capabilities.canChangeSubscription && isCurrent && subscription.uncoveredBranches.length > 0 && (
                      <div className="accountant-add-branch">
                        <Select
                          value={selectedNewBranch}
                          onChange={(event) =>
                            setAddBranchChoice((current) => ({ ...current, [subscription.id]: event.target.value }))
                          }
                          options={subscription.uncoveredBranches.map((branch) => ({ value: branch.branchId, label: branch.branchName }))}
                        />
                        <Button
                          appearance="soft"
                          onClick={() => {
                            const branch = subscription.uncoveredBranches.find((item) => item.branchId === selectedNewBranch);
                            if (branch) setSubscriptionModal({ type: "addBranch", subscription, branch });
                          }}
                        >
                          {t.subscriptions.addBranch}
                        </Button>
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        )}

        {subscriptionPages > 1 && (
          <Pagination
            className="accountant-no-print"
            page={Math.min(subscriptionPage, subscriptionPages)}
            pages={subscriptionPages}
            total={filteredSubscriptions.length}
            onPage={setSubscriptionPage}
            labels={paginationLabels}
          />
        )}

        {subscriptionModal?.type === "changePlan" && (
          <ChangePlanModal
            subscription={toBillingSubscription(subscriptionModal.subscription)}
            onCancel={closeSubscriptionModal}
            onChanged={() => {
              closeSubscriptionModal();
              billing.reload();
            }}
          />
        )}
        {subscriptionModal?.type === "addBranch" && (
          <AddBranchModal
            branch={{ branchId: subscriptionModal.branch.branchId, name: subscriptionModal.branch.branchName }}
            onCancel={closeSubscriptionModal}
            onConfirm={confirmAddBranch}
          />
        )}
        {subscriptionModal?.type === "extendBranch" && (
          <ExtendCoverageModal
            title={t.subscriptions.extendTitle}
            description={t.subscriptions.extendDescription}
            branches={[
              {
                branchId: subscriptionModal.branch.branchId,
                name: subscriptionModal.branch.branchName,
                currentEndDate: subscriptionModal.branch.endDate || subscriptionModal.subscription.endDate,
              },
            ]}
            onCancel={closeSubscriptionModal}
            onConfirm={confirmExtendBranch}
          />
        )}
      </section>

      <section className="accountant-section" aria-labelledby="accountant-analytics">
        <div className="accountant-section-head">
          <div>
            <h2 id="accountant-analytics">{t.analytics.title}</h2>
            <p>{t.analytics.description}</p>
          </div>
          <div className="accountant-period accountant-no-print">
            <Select
              label={t.analytics.year}
              value={analyticsYear}
              onChange={(event) => setAnalyticsYear(event.target.value)}
              options={analyticsYears.map((year) => ({ value: year, label: year }))}
            />
            <Select
              label={t.analytics.month}
              value={analyticsMonth}
              onChange={(event) => setAnalyticsMonth(event.target.value)}
              options={[
                { value: "", label: t.analytics.allMonths },
                ...t.analytics.months.map((label, index) => ({ value: String(index + 1), label })),
              ]}
            />
          </div>
        </div>

        {billing.analytics.status === "error" ? (
          <EmptyState variant="error" compact action={retry}>
            {t.analytics.error}
          </EmptyState>
        ) : billing.analytics.status === "loading" && !analyticsData ? (
          <EmptyState variant="loading" compact>
            {t.loading}
          </EmptyState>
        ) : (
          <div className="accountant-charts">
            <Card className="accountant-chart-card">
              <h3>{t.analytics.statusTitle}</h3>
              <AccountantDonutChart title={t.analytics.statusTitle} unit={t.analytics.invoices} items={statusItems} />
            </Card>
            <Card className="accountant-chart-card">
              <h3>{t.analytics.monthlyTitle}</h3>
              <AccountantBarChart ariaLabel={t.analytics.monthlyTitle} labels={monthlyRevenue.labels} values={monthlyRevenue.values} />
            </Card>
            <Card className="accountant-chart-card">
              <h3>{t.analytics.packageTitle}</h3>
              <AccountantBarChart ariaLabel={t.analytics.packageTitle} labels={packageRevenue.labels} values={packageRevenue.values} tone="package" />
            </Card>
            <Card className="accountant-chart-card">
              <h3>{t.analytics.agingTitle}</h3>
              <p className="accountant-muted">
                {t.analytics.agingNote}
                {analyticsData?.agingReferenceDate ? t.analytics.asOf(analyticsData.agingReferenceDate) : ""}
              </p>
              <AccountantBarChart ariaLabel={t.analytics.agingTitle} labels={t.analytics.aging} values={agingValues} tone="aging" />
            </Card>
          </div>
        )}
      </section>

      <div className="accountant-columns">
        <section className="accountant-section" aria-labelledby="accountant-followup">
          <div className="accountant-section-head">
            <h2 id="accountant-followup">{t.followUp.title}</h2>
            <Button appearance="outline" className="accountant-no-print" onClick={exportStoreData}>
              {t.followUp.export}
            </Button>
          </div>
          <dl className="accountant-facts is-stacked">
            {restaurant ? (
              <>
                <div>
                  <dt>{t.followUp.client}</dt>
                  <dd>
                    {storeName(selectedRestaurant)} ({selectedRestaurant.id})
                  </dd>
                </div>
                <div>
                  <dt>{t.followUp.package}</dt>
                  <dd data-testid="current-package">{currentPackage}</dd>
                </div>
                <div>
                  <dt>{t.followUp.lastPayment}</dt>
                  <dd data-testid="last-payment">{followUp?.lastPaymentDate || "—"}</dd>
                </div>
              </>
            ) : (
              <div>
                <dt>{t.followUp.restaurants}</dt>
                <dd>{restaurantsInView}</dd>
              </div>
            )}
            <div>
              <dt>{t.followUp.pending}</dt>
              <dd data-testid="pending-count">{pendingCount}</dd>
            </div>
          </dl>
          {!restaurant && <p className="accountant-muted">{t.followUp.pickOne}</p>}
        </section>

        <section className="accountant-section" aria-labelledby="accountant-notes">
          <div className="accountant-section-head">
            <h2 id="accountant-notes">{t.notes.title}</h2>
            <Button appearance="outline" className="accountant-no-print" disabled={!restaurant} onClick={() => setNoteModalOpen(true)}>
              {t.notes.add}
            </Button>
          </div>
          {noteError && (
            <NoticeBox variant="danger" size="sm">
              {t.notes.saveError}
            </NoticeBox>
          )}
          {!restaurant ? (
            <p className="accountant-muted">{t.notes.pickOne}</p>
          ) : directory.notes.status === "error" ? (
            <EmptyState variant="error" compact>
              {t.notes.error}
            </EmptyState>
          ) : notes.length ? (
            <ul className="accountant-notes">
              {notes.map((note) => (
                <li key={note.id}>
                  <div>
                    <p>{note.text}</p>
                    <small>
                      {note.date}
                      {note.invoiceRef ? t.notes.relatedInvoice(note.invoiceRef) : ""}
                    </small>
                  </div>
                  <Button
                    size="sm"
                    variant="danger"
                    appearance="soft"
                    className="accountant-no-print"
                    icon={<Trash2 size={16} />}
                    title={t.notes.delete}
                    aria-label={t.notes.delete}
                    onClick={() => setNoteDeleteId(note.id)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="accountant-muted">{directory.notes.status === "loading" ? t.loading : t.notes.empty}</p>
          )}

          <DashboardModal
            open={noteModalOpen}
            title={t.notes.add}
            onClose={() => setNoteModalOpen(false)}
            footer={
              <>
                <Button variant="primary" appearance="filled" onClick={addNote}>
                  {t.notes.save}
                </Button>
                <Button variant="danger" appearance="soft" onClick={() => setNoteModalOpen(false)}>
                  {t.notes.cancel}
                </Button>
              </>
            }
          >
            <Input textarea value={noteText} placeholder={t.notes.placeholder} onChange={(event) => setNoteText(event.target.value)} />
          </DashboardModal>

          {noteDeleteId && <DeleteModal id={noteDeleteId} handleDelete={deleteNote} cancel={setNoteDeleteId} />}
        </section>
      </div>
    </div>
  );
}
